import { Button, Card, Col, Form, Row, Skeleton } from "antd";
import dayjs from "dayjs";
import _cloneDeep from "lodash/cloneDeep";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import _set from "lodash/set";
import PropTypes from "prop-types";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import AppModal from "../../components/AppModal/AppModal";
import CustomIcon from "../../components/CustomIcon/CustomIcon";
import TransferTable from "../../components/TransferTable/TransferTable";
import buildingService from "../../services/building.service";
import { refreshFeatureFlagsAfterBudSyncDisabled } from "../../store/actions/featureFlag.actions";
import { selectPropertySearchRedesignClientState } from "../../store/selectors/featureFlag.selector";
import { ERROR_MESSAGE, PROPERTY_CHARACTERISTICS, PROPERTY_STATUS, SUCCESS_MESSAGE } from "../../utils/constants";
import { to, toastMessage } from "../../utils/utils";
import {
  buildCharacteristicsRows,
  buildSyncRows,
  formatRefreshedSyncRows,
  getBuildingSyncFields,
  getBuildingSyncUpdates,
} from "./characteristicsSync.util";
import "./Characteristics.scss";
import {
  BUILDING_DIMENSION_FIELDS,
  BUILDING_DIMENSION_LABEL_KEYS,
  getDimensionAriaLabel,
  getSelectedDimensionBlock,
} from "./dimensions.util";

const normalizeBuildingInfoForForm = buildingInfo => {
  const nextBuildingInfo = { ...buildingInfo };
  if (nextBuildingInfo.availableDate) {
    nextBuildingInfo.availableDate = dayjs(nextBuildingInfo.availableDate);
  }
  return nextBuildingInfo;
};

const getFormSyncValue = value => (value === null ? undefined : value);
const getBuildingFormFieldPath = row => {
  if (row.id === "clearHeight") return undefined;
  if (Object.prototype.hasOwnProperty.call(row, "formPath")) {
    return row.formPath ? row.formPath.replace("buildingInfo.", "") : undefined;
  }
  return row.path?.replace("buildingInfo.", "");
};
const labelInValueFieldPaths = ["propertyCode", "propertyRollUp"];
const booleanBuildingFieldIds = ["railServed", "crossDock"];
const legacyBuildingSyncFieldIds = [
  "railServed",
  "truckCourtDepth",
  "subMarketId",
  "latitude",
  "longitude",
  "marketId",
  "totalBuildingSize",
  "totalBuildingSizeUOM",
  "marketingPropertyName",
];

const getLabelInValueFormSyncValue = value => {
  const formValue = getFormSyncValue(value);
  if (
    formValue === undefined ||
    (typeof formValue === "object" && Object.prototype.hasOwnProperty.call(formValue, "value"))
  ) {
    return formValue;
  }
  const optionValue =
    typeof formValue === "object" ? formValue.id || formValue.label || formValue.name || formValue.code : formValue;
  return { label: optionValue, value: optionValue };
};

const getBuildingFormSyncValue = row => {
  const value = getFormSyncValue(row.updatedValue);
  if (!booleanBuildingFieldIds.includes(row.id) || typeof value === "boolean" || value === undefined) return value;
  const normalizedValue = `${value}`.trim().toLowerCase();
  if (["1", "yes", "true", "y"].includes(normalizedValue)) return true;
  if (["0", "no", "false", "n"].includes(normalizedValue)) return false;
  return value;
};

const getLegacyBuildingDpmValue = row => {
  if (row.id === "railServed") return row.dpmValue ? "Yes" : "No";
  if (row.id === "truckCourtDepth" && ["0", "nan"].includes(`${row.dpmValue}`.trim().toLowerCase())) {
    return undefined;
  }
  return row.dpmValue;
};

const getLegacyBuildingBudValue = row => {
  if (row.id === "railServed") return row.budValue === "1" ? "Yes" : "No";
  return row.budValue;
};

const getBudSyncErrorMessage = (err, fallback) => _get(err, "response.data.message", err?.message || fallback);

const setBuildingClearHeightFormValues = (buildingInfo, value) => {
  if (!value || typeof value !== "object") return;
  ["minimum", "maximum"].forEach(rangeKey => {
    const prefix = `${rangeKey}ClearHeight`;
    if (value[rangeKey] === null || value[rangeKey] === undefined) {
      buildingInfo[prefix] = undefined;
      buildingInfo[`${prefix}Feet`] = undefined;
      buildingInfo[`${prefix}Inches`] = undefined;
      return;
    }
    const numericValue = Number(value[rangeKey]);
    if (!Number.isFinite(numericValue)) return;
    const totalInches = Math.round(numericValue * 12);
    buildingInfo[prefix] = numericValue;
    buildingInfo[`${prefix}Feet`] = Math.floor(totalInches / 12);
    buildingInfo[`${prefix}Inches`] = totalInches % 12;
  });
};

const applyBuildingRowsToFormInfo = (buildingInfo, rows) => {
  const nextBuildingInfo = _cloneDeep(buildingInfo || {});
  rows.forEach(row => {
    if (row.id === "clearHeight") {
      setBuildingClearHeightFormValues(nextBuildingInfo, row.updatedValue);
      return;
    }
    const fieldPath = getBuildingFormFieldPath(row);
    if (!fieldPath) return;
    const syncValue = labelInValueFieldPaths.includes(fieldPath)
      ? getLabelInValueFormSyncValue(row.updatedValue)
      : getBuildingFormSyncValue(row);
    _set(nextBuildingInfo, fieldPath, syncValue);
  });
  return normalizeBuildingInfoForForm(nextBuildingInfo);
};

const PropertyCharacteristics = ({
  data,
  codeDetail,
  selected,
  form,
  notAvailable,
  isTemporary,
  viewOnlyMode,
  updateNotAvailableFields,
  dimensionDisplayProps,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isDifferenceModalOpen, setIsDifferenceModalOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const FEATURE_FLAG = useSelector(state => state.featureFlag?.value || {});
  const propertySearchRedesignState = useSelector(selectPropertySearchRedesignClientState);
  const lastResolvedBudSyncState = useRef(propertySearchRedesignState === true);
  const isFeatureFlagResolved = propertySearchRedesignState !== undefined;
  if (isFeatureFlagResolved) lastResolvedBudSyncState.current = propertySearchRedesignState;
  const isBudSyncEnabled = isFeatureFlagResolved ? propertySearchRedesignState : lastResolvedBudSyncState.current;
  const isSubmarketEnabled = Boolean(FEATURE_FLAG.SUBMARKET?.status);
  const _onCancel = () => setIsDifferenceModalOpen(false);
  const syncFields = useMemo(
    () =>
      getBuildingSyncFields(t, {
        property_search_redesign: { status: isBudSyncEnabled },
        SUBMARKET: { status: isSubmarketEnabled },
      }),
    [t, isBudSyncEnabled, isSubmarketEnabled],
  );

  const getDpmSource = () => ({
    ...selected,
    buildingInfo: {
      ...selected?.buildingInfo,
      ...form.getFieldValue("buildingInfo"),
    },
  });

  const getPreviewRows = async () => {
    if (!isFeatureFlagResolved || !isBudSyncEnabled) return [];
    setSyncLoading(true);
    const rows = buildSyncRows({
      fields: syncFields,
      budSource: codeDetail,
      dpmSource: getDpmSource(),
    });
    const [err, response] = await to(buildingService.previewBudSync(selected?.id, { rows, recordType: "building" }));
    setSyncLoading(false);
    if (err) {
      await dispatch(refreshFeatureFlagsAfterBudSyncDisabled(err));
      toastMessage("error", getBudSyncErrorMessage(err, ERROR_MESSAGE.INFO));
      return [];
    }
    return formatRefreshedSyncRows(response?.rows, syncFields);
  };

  const getLegacySyncRows = () =>
    buildSyncRows({
      fields: syncFields.filter(field => legacyBuildingSyncFieldIds.includes(field.key)),
      budSource: codeDetail,
      dpmSource: getDpmSource(),
    });

  const getLegacySource = () =>
    getLegacySyncRows().reduce(
      (source, row) => ({
        ...source,
        [row.id]: {
          label: row.fieldName,
          value: getLegacyBuildingBudValue(row),
        },
      }),
      {},
    );

  const getLegacyCurrent = () =>
    getLegacySyncRows().reduce(
      (current, row) => ({
        ...current,
        [row.id]: getLegacyBuildingDpmValue(row),
      }),
      {},
    );

  const _onLegacySuccess = values => {
    if (!isFeatureFlagResolved) return;
    const selectedRows = getLegacySyncRows()
      .filter(row => Object.prototype.hasOwnProperty.call(values, row.id))
      .map(row => ({
        ...row,
        updatedValue: row.id === "railServed" ? values[row.id] === "Yes" : values[row.id],
      }));

    form.setFieldsValue({
      buildingInfo: applyBuildingRowsToFormInfo(form.getFieldValue("buildingInfo"), selectedRows),
    });
    selectedRows.forEach(row => {
      const fieldName = getBuildingFormFieldPath(row);
      if (fieldName && notAvailable[fieldName]) updateNotAvailableFields(fieldName);
    });
    setIsDifferenceModalOpen(false);
  };

  const _onApplyRows = async rows => {
    if (!isFeatureFlagResolved || !isBudSyncEnabled) return;
    setSyncLoading(true);
    const updates = getBuildingSyncUpdates(rows);
    const [err] = await to(buildingService.applyBudSync(selected?.id, { recordType: "building", updates }));
    setSyncLoading(false);
    if (err) {
      await dispatch(refreshFeatureFlagsAfterBudSyncDisabled(err));
      toastMessage("error", getBudSyncErrorMessage(err, ERROR_MESSAGE.EDITED));
      return;
    }

    form.setFieldsValue({
      buildingInfo: applyBuildingRowsToFormInfo(form.getFieldValue("buildingInfo"), rows),
    });
    rows.forEach(row => {
      const fieldName = getBuildingFormFieldPath(row);
      if (fieldName && notAvailable[fieldName]) updateNotAvailableFields(fieldName);
    });
    setIsDifferenceModalOpen(false);
    toastMessage("success", SUCCESS_MESSAGE.EDITED);
  };

  const _disabled =
    !isFeatureFlagResolved ||
    viewOnlyMode ||
    _isEmpty(codeDetail) ||
    _isEmpty(selected) ||
    (selected?.status === PROPERTY_STATUS.never_published && !selected.isDraft) ||
    isTemporary;

  const renderCharacteristicValue = characteristic => {
    const dimensionField = BUILDING_DIMENSION_FIELDS[characteristic.value];
    if (!dimensionDisplayProps || !dimensionField) {
      return `: ${_get(data, characteristic.value, "-")}`;
    }

    const { dimensions, dimensionsError, dimensionsLoading, selectedDimensionUnitSystem } = dimensionDisplayProps;
    if (dimensionsLoading) return <Skeleton.Input active size="small" />;
    if (dimensionsError) return ": -";

    const dimension = _get(dimensions, `building.${dimensionField}`);
    const selectedBlock = getSelectedDimensionBlock(dimension, selectedDimensionUnitSystem);
    if (!selectedBlock?.formatted) return ": -";

    return (
      <span aria-label={getDimensionAriaLabel(dimension, selectedDimensionUnitSystem)}>
        {`: ${selectedBlock.formatted}`}
      </span>
    );
  };

  const displayedCharacteristics = dimensionDisplayProps
    ? [{ label: t("UnitCharacteristics.ClearHeight"), value: "clearHeight" }, ...PROPERTY_CHARACTERISTICS]
    : PROPERTY_CHARACTERISTICS;

  return (
    <>
      <Card
        title={t("PropertyCharacteristics.Title")}
        className="overflow-auto characteristics"
        extra={
          <Button
            disabled={_disabled}
            size="large"
            shape={isBudSyncEnabled ? undefined : "circle"}
            type="primary"
            icon={<CustomIcon name="SyncOutlined" />}
            onClick={() => setIsDifferenceModalOpen(true)}
          >
            {isBudSyncEnabled && t("TransferTable.SyncWithBud")}
          </Button>
        }
      >
        {isBudSyncEnabled ? (
          <Form.Item noStyle shouldUpdate>
            {() => {
              const rows = buildCharacteristicsRows({ fields: syncFields, source: getDpmSource() });
              const dimensionOrder = displayedCharacteristics.map(characteristic => characteristic.value);
              const dimensionRows = rows
                .filter(characteristic => BUILDING_DIMENSION_FIELDS[characteristic.key])
                .sort((left, right) => dimensionOrder.indexOf(left.key) - dimensionOrder.indexOf(right.key));
              const nonDimensionRows = rows.filter(characteristic => !BUILDING_DIMENSION_FIELDS[characteristic.key]);

              return [...dimensionRows, ...nonDimensionRows].map(characteristics => {
                const dimensionCharacteristic = {
                  label: characteristics.label,
                  value: characteristics.key,
                };
                return (
                  <Row key={characteristics.key}>
                    <Col md={12}>
                      {dimensionDisplayProps && BUILDING_DIMENSION_LABEL_KEYS[characteristics.key]
                        ? t(BUILDING_DIMENSION_LABEL_KEYS[characteristics.key])
                        : characteristics.label}
                    </Col>
                    <Col md={12}>
                      {BUILDING_DIMENSION_FIELDS[characteristics.key]
                        ? renderCharacteristicValue(dimensionCharacteristic)
                        : `: ${characteristics.value}`}
                    </Col>
                  </Row>
                );
              });
            }}
          </Form.Item>
        ) : (
          displayedCharacteristics.map(characteristics => (
            <Row key={characteristics.value}>
              <Col md={12}>
                {dimensionDisplayProps && BUILDING_DIMENSION_LABEL_KEYS[characteristics.value]
                  ? t(BUILDING_DIMENSION_LABEL_KEYS[characteristics.value])
                  : characteristics.label}
              </Col>
              <Col md={12}>{renderCharacteristicValue(characteristics)}</Col>
            </Row>
          ))
        )}
      </Card>
      <AppModal
        title={t("PropertyCharacteristics.AppModalTitle")}
        width={900}
        visible={isDifferenceModalOpen}
        onCancel={_onCancel}
        footer={null}
        maxHeight="80vh"
        overFlowY="auto"
        destroyOnClose
      >
        <TransferTable
          key={isBudSyncEnabled ? "bud-sync" : "legacy"}
          onCancel={_onCancel}
          loading={syncLoading || !isFeatureFlagResolved}
          {...(isBudSyncEnabled
            ? { onPreview: getPreviewRows, onApplyRows: _onApplyRows }
            : { source: getLegacySource(), current: getLegacyCurrent(), onSuccess: _onLegacySuccess })}
        />
      </AppModal>
    </>
  );
};

PropertyCharacteristics.propTypes = {
  data: PropTypes.object,
  codeDetail: PropTypes.object,
  selected: PropTypes.object,
  form: PropTypes.object,
  notAvailable: PropTypes.object,
  updateNotAvailableFields: PropTypes.func,
  isTemporary: PropTypes.bool,
  viewOnlyMode: PropTypes.bool,
  dimensionDisplayProps: PropTypes.object,
};

export default PropertyCharacteristics;
